import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assessment as SurveyIcon,
  Security as SecurityIcon,
  Gavel as GavelIcon,
  ExitToApp as OffboardingIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Home as HomeIcon,
  Feedback as FeedbackIcon,
  Pending as PendingIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { useSearchParams } from 'react-router-dom'; 
// Import the admin-specific tab components
import AdminDocumentsTab from '../components/admin-existing/AdminDocumentsTab';
import AdminFeedbackTab from '../components/admin-existing/AdminFeedbackTab';
import AdminKpikriTab from '../components/admin-existing/AdminKpikriTab';
import AdminBrandSurveyTab from '../components/admin-existing/AdminBrandSurveyTab';
import AdminGrievanceFormTab from '../components/admin-existing/AdminGrievanceFormTab';
import AdminPoshComplaintTab from '../components/admin-existing/AdminPoshComplaintTab';
import AdminMyTeamTab from '../components/admin-existing/AdminMyTeamTab';
import AdminAccessProvisioningTab from '../components/admin-existing/AdminAccessProvisioningTab';



const AdminExistingEmployee = () => {
  const { user, tokenManager } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOption, setSelectedOption] = useState('documents');
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  // Admin-specific options using admin components
  const options = [
    { key: 'documents', label: 'Documents', icon: <DescriptionIcon />, component: AdminDocumentsTab },
    { key: 'feedback', label: 'Feedback', icon: <FeedbackIcon />, component: AdminFeedbackTab },
    { key: 'my_team', label: 'My Team', icon: <GroupIcon />, component: AdminMyTeamTab },
    { key: 'kpi_kri', label: 'KPI, KRA', icon: <PendingIcon />, component: AdminKpikriTab, disabled: true },
    { key: 'brand_survey', label: 'Brand Survey', icon: <PendingIcon />, component: AdminBrandSurveyTab , disabled: true },
    { key: 'grievance_form', label: 'Grievance Form', icon: <PendingIcon />, component: AdminGrievanceFormTab },
    { key: 'posh_complaint', label: 'POSH Complaint', icon: <PendingIcon />, component: AdminPoshComplaintTab },
    { key: 'access_provisioning', label: 'Access Provisioning', icon: <SecurityIcon />, component: AdminAccessProvisioningTab },
  ];

  useEffect(() => {
    if (user?.primaryRole === 'admin' || user?.primaryRole === 'itsupport') {
      fetchUsers();
    } else {
      // Redirect non-admin users to regular existing employee page
      navigate('/existing');
    }
  }, [user, navigate]);

  // Auto-select user based on email parameter
  useEffect(() => {
    if (email && users.length > 0) {
      const userWithEmail = users.find(u => u.email === email);
      if (userWithEmail) {
        setSelectedUser(userWithEmail);
        setSelectedOption('access_provisioning'); // Auto-select Access Provisioning tab
        console.log('Auto-selected user based on email:', userWithEmail);
        console.log('Auto-selected Access Provisioning tab');
      } else {
        console.warn(`No user found with email: ${email}`);
        toast.warning(`No user found with email: ${email}`);
      }
    }
  }, [email, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('API Response:', data);
        // The API returns users directly as an array, not wrapped in a users property
        const usersArray = Array.isArray(data) ? data : (data.users || []);
        console.log('Users Array:', usersArray);
        setUsers(usersArray);
        // Set the first user as default selection only if no email parameter is provided
        if (usersArray.length > 0 && !email) {
          setSelectedUser(usersArray[0]);
          console.log('Selected User:', usersArray[0]);
        }
      } else {
        setError('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (event) => {
    const userId = event.target.value;
    const user = users.find(u => (u._id || u.id) === userId);
    setSelectedUser(user);
  };

  const handleNavigateToRegularEmployee = () => {
    navigate('/existing');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // If no user is found yet
  if (!user) return <div>Loading...</div>;
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>

        <Box sx={{ mb: 4, mt: 2 }}>
          <Typography gutterBottom sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1, fontSize: 30 }}>
            Admin Portal - Employee Management
          </Typography>
          <Typography color="text.secondary" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 400, mb: 3, fontSize: 20 }}>
            Manage employee data and view their information
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          {/* User Selection Dropdown */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Select Employee to View
            </Typography>
            <br />
            <FormControl fullWidth sx={{ mb: 2, maxWidth: 600 }}>
              <InputLabel>Select Employee</InputLabel>
              <Select
                value={selectedUser?._id || selectedUser?.id || ''}
                onChange={handleUserChange}
                label="Select Employee"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                      maxWidth: 600,
                      overflow: 'auto'
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null
                }}
              >
                <MenuItem value="">
                  <em>Select an employee</em>
                </MenuItem>
                {users.length > 0 ? (
                  users.map((user) => (
                    <MenuItem key={user._id || user.id} value={user._id || user.id}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        minWidth: 0,
                        width: '100%'
                      }}>
                        <PeopleIcon fontSize="small" sx={{ flexShrink: 0 }} />
                        <Box sx={{ 
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.firstName || 'N/A'} {user.lastName || 'N/A'} - {user.email || 'N/A'}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {loading ? 'Loading users...' : 'No users available'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            

            {/* <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Chip 
                label={`Admin: ${user?.firstName} ${user?.lastName}`} 
                color="primary" 
                variant="outlined"
              />
            </Box> */}
          </Box>

          {selectedUser ? (
            <>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
                This section allows you to view and manage {selectedUser.firstName}'s documents, trainings, feedback, grievances, and other employee data.
              </Typography>

              {/* Option Cards */}
              <Grid
                container
                spacing={2}
                justifyContent={isMobile ? 'center' : 'flex-start'}
                sx={{ mb: 4 }}
              >
                {options.map((option) => (
                  <Grid item xs={6} sm="auto" key={option.key}>
                    <Card
                      sx={{
                        minWidth: 160,
                        minHeight: 130,
                        boxShadow: selectedOption === option.key ? 6 : 1,
                        backgroundColor: option.disabled 
                          ? 'grey.100' 
                          : selectedOption === option.key 
                            ? 'primary.100' 
                            : 'background.paper',
                        color: option.disabled 
                          ? 'grey.500' 
                          : selectedOption === option.key 
                            ? 'primary.main' 
                            : 'text.primary',
                        border: selectedOption === option.key ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        transition: 'all 0.2s',
                        cursor: option.disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 1,
                        opacity: option.disabled ? 0.6 : 1,
                      }}
                      onClick={() => !option.disabled && setSelectedOption(option.key)}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                        {option.icon}
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 500, fontSize: '0.9rem' }}>
                          {option.label}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Selected Tab Content */}
              <Box sx={{ mt: 2 }}>
                {options.find((o) => o.key === selectedOption)?.component &&
                  React.createElement(options.find((o) => o.key === selectedOption).component, {
                    user: selectedUser // Pass the selected user to the component
                  })}
              </Box>
            </>
          ) : (
            <Alert severity="warning">
              Please select an employee to view their data.
            </Alert>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminExistingEmployee;
